/**
 * @deprecated 예정
 */
import { useEffect, useRef, useState } from 'react';

import {
  Bar,
  CartesianGrid,
  BarChart as ReChartsBarChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import styled from 'styled-components';

type Metrics = {
  name: string;
  value: number;
};

type BarChartProps = {
  metrics: Metrics[];
  negativeValueColor?: string;
  positiveValueColor?: string;
  highestValueColor?: string;
  lowestValueColor?: string;
};
export default function BarChart({
  metrics,
  negativeValueColor = '#FE008A',
  positiveValueColor = '#B0EAF4',
  highestValueColor = '#00C3DD',
  lowestValueColor = '#B0EAF4',
}: BarChartProps) {
  const highestValue = Math.max(...metrics.map((o) => o.value));
  const lowestValue = Math.min(...metrics.map((o) => o.value));

  // Tooltip 위치를 위한 상태값
  const [activateBar, setActivateBar] = useState<Partial<MetricData>>({});
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);

  const selectBarColor = (value: number) => {
    if (value === highestValue) return highestValueColor;
    if (value === lowestValue) return lowestValueColor;
    return value > 0 ? positiveValueColor : negativeValueColor;
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // 마우스 이벤트 핸들러
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.pageX - (containerRef.current?.offsetLeft || 0));
    setScrollLeft(containerRef.current?.scrollLeft || 0);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - (containerRef.current?.offsetLeft || 0);
    const walk = (x - startX) * 1; // 드래그 속도 조절 (1은 기본값)
    if (containerRef.current) {
      containerRef.current.scrollLeft = scrollLeft - walk;
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // 터치 이벤트 핸들러
  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setStartX(e.touches[0].pageX - (containerRef.current?.offsetLeft || 0));
    setScrollLeft(containerRef.current?.scrollLeft || 0);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const x = e.touches[0].pageX - (containerRef.current?.offsetLeft || 0);
    const walk = (x - startX) * 1;
    if (containerRef.current) {
      containerRef.current.scrollLeft = scrollLeft - walk;
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // 위쪽 마진을 위한 값 (최대값의 10%를 마진으로 적용)
  const marginTop = Math.floor(highestValue * 0.1);
  const yMax = highestValue + marginTop;

  // 눈금 간격을 계산 (5개의 눈금으로 설정)
  const tickCount = 9;
  const tickInterval = Math.ceil(yMax / (tickCount - 1));
  const yTicks =
    metrics.length === 0
      ? Array.from({ length: tickCount }, (_, i) => i * 25000) // default y ticks
      : Array.from({ length: tickCount }, (_, i) => i * tickInterval);

  return (
    <SBarChartContainer
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <ResponsiveContainer
        width={'100%'}
        minHeight={304}
        style={{
          position: 'relative',
        }}
      >
        <>
          <ReChartsBarChart
            width={1166}
            height={304}
            data={metrics}
            margin={{
              // top: 23,
              right: 30,
              // left: 20,
              // bottom: 5,
            }}
          >
            <XAxis
              dataKey="name"
              tickCount={metrics.length}
              tickMargin={10}
              fontSize={11}
              color={'#8A8A8A'}
              interval={0}
              tickLine={false}
            />
            <YAxis
              tickCount={tickCount}
              ticks={yTicks}
              tickFormatter={(value) => value.toLocaleString()}
              fontSize={11}
              fontWeight={600}
              color="#8A8A8A"
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              cursor={{ fill: 'transparent' }}
              content={<CustomTooltip highestValue={highestValue} />}
              allowEscapeViewBox={{ x: true, y: true }}
              position={{ y: (activateBar?.y ?? 0) - 31 }}
            />

            <CartesianGrid vertical={false} stroke="#F1F1F4" />
            <Bar
              dataKey="value"
              maxBarSize={10}
              shape={(props: any) => {
                const { x, y, width, value, amountFormat, amount } = props;
                let { height } = props;

                // 최소 높이 적용
                const minHeight = 1;
                if (height === 0) {
                  height = minHeight;
                }

                const fillColor = selectBarColor(value);
                const barY = value >= 0 ? y - (height === minHeight ? minHeight : 0) : y + height;

                // 텍스트 길이에 맞게 동적으로 너비를 계산 (최소 80px 보장)
                const textWidth = amountFormat.length * 8 + 24;
                const markWidth = Math.max(textWidth, 80);
                const markHeight = 29; // 원래 높이 36px * 0.8

                // 툴팁 위치 조정
                const markX = x + 4 - markWidth / 2; // 막대의 중앙
                const markY = barY - 36;

                return (
                  <>
                    {amount === highestValue && (
                      <g transform={`translate(${markX}, ${markY})`}>
                        <HighestValueMark value={amountFormat} width={markWidth} height={markHeight} />
                      </g>
                    )}
                    <rect x={x} y={barY} width={8} height={height} fill={fillColor} radius={3} />
                  </>
                );
              }}
              radius={[3, 3, 0, 0]}
              onMouseEnter={(data) => {
                setActivateBar(data);
                // const { x, y, width } = data;
                // console.log(data, x, y, width);
                // setTooltipPosition({ x: x + width / 2, y: y - 40 });
              }}
              onMouseLeave={(data) => {
                setActivateBar(data);
                // setActivateBar({});
                // setTooltipPosition(null);
              }}
            />
          </ReChartsBarChart>
        </>
      </ResponsiveContainer>
    </SBarChartContainer>
  );
}

const SBarChartContainer = styled.section`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;

  &::-webkit-scrollbar {
    display: none;
  }
  &:hover {
    height: auto;
  }

  .recharts-wrapper {
    overflow: visible;
  }
`;
interface TooltipPayload {
  radius: [number, number, number, number];
  dataKey: string;
  name: string;
  value: number;
  payload: Payload;
  hide: boolean;
}

interface TooltipPosition {
  x: number;
  y: number;
}

interface Background {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Payload {
  metricDatetime: string;
  amount: number;
  amountFormat: string;
  value: number;
  name: string;
}

interface MetricData {
  metricDatetime: string;
  amount: number;
  amountFormat: string;
  value: number;
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  payload: Payload;
  background: Background;
  tooltipPayload: TooltipPayload[];
  tooltipPosition: TooltipPosition;
}
const HighestValueMark = ({ value, width, height }: { value: string; width: number; height: number }) => {
  const tailSize = 5; // 원래 꼬리 크기 6px * 0.8

  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={width} height={height + tailSize}>
      {/* 말풍선 본체와 꼬리 */}
      <path
        d={`
          M14,0 
          H${width - 14} 
          A14,14 0 0 1 ${width},14 
          V${height - 14} 
          A14,14 0 0 1 ${width - 14},${height} 
          H${width / 2 + tailSize} 
          L${width / 2},${height + tailSize} 
          L${width / 2 - tailSize},${height} 
          H14 
          A14,14 0 0 1 0,${height - 14} 
          V14 
          A14,14 0 0 1 14,0
        `}
        fill="#00C3DD"
      />

      {/* 아이콘과 텍스트 그룹 */}
      <g transform={`translate(${width / 2}, ${height / 2})`} textAnchor="middle">
        {/* 아이콘 */}
        <svg width="13" height="13" x="-32" y="-6.5" viewBox="0 0 20 20" fill="none">
          <path
            d="M2 17.1579C2 16.6928 2.37702 16.3158 2.84211 16.3158H17.1579C17.623 16.3158 18 16.6928 18 17.1579C18 17.623 17.623 18 17.1579 18H2.84211C2.37702 18 2 17.623 2 17.1579ZM2 6.34064C2 5.55309 2.86812 5.07461 3.53399 5.49515L6 7.05263L9.21596 2.99037C9.61633 2.48464 10.3837 2.48464 10.7841 2.99037L14 7.05263L16.466 5.49515C17.1319 5.07461 18 5.55309 18 6.34064V13.6316C18 14.1839 17.5523 14.6316 17 14.6316H3C2.44772 14.6316 2 14.1839 2 13.6316V6.34064Z"
            fill="#FFFFFF"
          />
        </svg>

        {/* 텍스트 */}
        <text x="8" y="4" fontSize="11" fontWeight="700" fill="#FFFFFF" fontFamily="Arial, sans-serif">
          {value}
        </text>
      </g>
    </svg>
  );
};

type CustomTooltipProps = TooltipProps<number, string> & {
  highestValue: number;
};

const CustomTooltip = ({ active, payload, highestValue }: CustomTooltipProps) => {
  if (!(active && payload && payload.length)) return null;
  const { amountFormat, amount } = payload[0].payload;
  const isHightestValue = amount === highestValue;
  // 가장 높은 값은 마커로 표시되므로 툴팁은 표시되지 않는다.
  if (isHightestValue) return null;
  if (amount === 0) return null;

  return (
    <SCustomTooltipContainer>
      <SCustomTooltipContent>
        <span className="text">{amountFormat}</span>
      </SCustomTooltipContent>

      <svg xmlns="http://www.w3.org/2000/svg" width="7" height="4" viewBox="0 0 7 4" fill="none">
        <g filter="url(#filter0_d_18454_2425)">
          {/* <path d="M3.5 1.5L1.5 0H5.5L3.5 1.5Z" fill="#333333" /> */}
          <path d="M3.5 4L0 0H7L3.5 4Z" fill="#333333" />
        </g>
        <defs>
          <filter
            id="filter0_d_18454_2425"
            x="-3"
            y="-2"
            width="13"
            height="10"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="1" />
            <feGaussianBlur stdDeviation="1" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0.121569 0 0 0 0 0.184314 0 0 0 0 0.27451 0 0 0 0.12 0" />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_18454_2425" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_18454_2425" result="shape" />
          </filter>
        </defs>
      </svg>
    </SCustomTooltipContainer>
  );
};

const SCustomTooltipContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 50px;
  height: 29px;
  transform: translateX(-36px);
`;

const SCustomTooltipContent = styled.div`
  background: rgba(0, 0, 0, 0.8);
  padding: 0px 6px;
  border-radius: 4px;
  height: 25px;
  display: flex;
  align-items: center;

  .text {
    color: white;
    font-family: Pretendard;
    font-size: 11px;
    font-style: normal;
    font-weight: 500;
    line-height: 17px;
  }
`;
